import React, { useEffect, useState } from 'react';

import { Box } from '@wds/box';
import { Typography } from '@wds/typography';
import { ProgressCircle } from '@wds/progress-circle';
import { Input } from '@wds/input';
import { Button } from '@wds/button';
import { Slide } from '@wds/motion';

import { getCookie } from 'sma.common/utils';

import PropTypes from 'prop-types';
import STRINGS from '../constants';

const TOTPEnrollment = (props) => {
	const {
		isLoaded,
		context,
		setContext,
		error,
		isSliding,
		setIsSliding,
		handleChangeMethod,
		handleErrorResponse,
		handleError,
		handleTryNewQR,
		currentPage,
		setCurrentPage,
		isChange,
	} = props;

	const [isValid, setIsValid] = useState(false);
	const [showSecurityKey, setShowSecurityKey] = useState(false);
	const [showTryAnotherMethod, setShowTryAnotherMethod] = useState(false);

	useEffect(() => {
		if (context?.methods.length > 0) {
			if (context.methods[0].method === 0) {
				setShowTryAnotherMethod(true);
			}
		}
	}, [context]);

	useEffect(() => {
		if (context.challengeCode.match(/^[0-9]+$/) && context.challengeCode.length === 6) {
			setIsValid(true);
		} else {
			setIsValid(false);
		}
	}, [context.challengeCode]);

	const onHandleContextChange = (key) => (e) => {
		setContext({ ...context, [key]: e.target.value });
	};

	const handleShowSecurityKey = () => {
		setShowSecurityKey(!showSecurityKey);
	};

	const handleChangeSecurityKeyView = () => {
		if (showSecurityKey) {
			setContext((prev) => ({ ...prev, qrCodeString: '', securityKeyString: '' }));
			handleTryNewQR();
			setShowSecurityKey(false);
		} else {
			setShowSecurityKey(true);
		}
	};

	const submitChallengeCode = async () => {
		try {
			let url;
			let action;
			if (isChange) {
				url = reverse('api-internal-mfa-change-device');
				action = 'complete_device_change';
			} else {
				url = reverse('api-internal-mfa-enroll');
				action = 'verify_totp_enrollment';
			}
			const response = await fetch(url, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'X-CSRFToken': getCookie('csrftoken'),
				},
				body: JSON.stringify({
					action,
					challenge_code: context.challengeCode,
					remember_device: context.rememberDevice,
					method_id: context.method_id,
					method: 'totp',
				}),
			});
			if (!response.ok) {
				handleErrorResponse(response);
				return;
			}
			const data = await response.json();
			if (data.error) {
				handleError(data.error);
				return;
			}
			if (data.recovery_codes) {
				setContext((prev) => ({
					...prev,
					recovery_codes: data.recovery_codes,
				}));
				setCurrentPage('enrollment-complete');
			}
		} catch (err) {
			handleError(`Error submitting challenge code: ${err}`);
		}
	};

	const inputColor = error ? 'error' : '';

	return (
		<Slide
			in={isLoaded && currentPage === 'totp-enrollment'}
			speed={currentPage === 'totp-enrollment' ? 'slow' : undefined}
			timeout={currentPage === 'totp-enrollment' ? undefined : 150}
			direction='right'
			onEnter={() => setIsSliding(true)}
			onExited={() => setIsSliding(false)}
			alwaysRenderContent
		>
			<Box
				display='flex'
				flexDirection='column'
				gap={4}
				style={{ position: isSliding ? 'absolute' : 'static', width: '100%' }}
			>
				<Box
					display='flex'
					flexDirection='column'
					justifyContent='center'
					alignItems='center'
				>
					<Typography variant='sectionTitleSm' align='center'>
						{isChange ? (
							STRINGS.changeMFADevice
						) : (
							STRINGS.setUpMFA
						)}
					</Typography>

					{showSecurityKey ? (
						<>
							<Typography variant='bodySm' align='center'>
								{STRINGS.useSecurityKey}
							</Typography>
							<Box
								display='flex'
								justifyContent='center'
								alignItems='center'
								borderColor='muted'
								p={2}
								style={{ textAlign: 'center', backgroundColor: '#e1e1e1', borderRadius: '5px' }}
							>
								{ context.securityKeyString }
							</Box>
							<Typography
								variant='link'
								component='a'
								align='center'
								href='#'
								onClick={handleChangeSecurityKeyView}
								underline='hover'
							>
								{STRINGS.tryQR}
							</Typography>
						</>
					) : (
						<>
							<Typography variant='bodySm' align='center'>
								{STRINGS.scanQR}
							</Typography>
							<Box
								display='flex'
								justifyContent='center'
								alignItems='center'
								style={{
									width: '170px',
									height: '170px',
								}}
								borderColor='muted'
							>
								{/* eslint-disable-next-line no-nested-ternary */}
								{context.qrCodeString ? (
									<img
										src={`data:image/png;base64,${context.qrCodeString}`}
										alt='QR Code'
										style={{ width: '168px', height: '168px' }}
									/>
								) : (
									error ? (
										<img
											className='sma-logo-powered'
											src='/media/img/smapply/goldie_black_with_border.png'
											alt='SurveyMonkey Apply logo'
											style={{ maxWidth: '150px', margin: 25, opacity: 0.2 }}
										/>
									) : (
										<ProgressCircle continuous />
									)
								)}
							</Box>
							<Typography
								variant='link'
								component='a'
								align='center'
								href='#'
								onClick={handleShowSecurityKey}
								underline='hover'
							>
								{STRINGS.troubleScanning}
							</Typography>
						</>
					)}
				</Box>
				<Box display='flex' alignItems='center' width='100%'>
					<hr
						style={{
							flex: 1,
							border: 'none',
							borderTop: '1px solid #cccccc80',
							margin: 0,
						}}
					/>
					<Box px={2}>
						<Typography
							variant='caption'
							style={{ textTransform: 'uppercase' }}
						>
							{STRINGS.then}
						</Typography>
					</Box>
					<hr
						style={{
							flex: 1,
							border: 'none',
							borderTop: '1px solid #cccccc80',
							margin: 0,
						}}
					/>
				</Box>
				<Box
					display='flex'
					flexDirection='column'
					gap={4}
					pb={6}
				>
					{error && (
						<Box
							p={4}
							style={{
								backgroundColor: '#AC145A',
								borderRadius: '5px',
								color: '#FFFFFF',
							}}
							role='alert'
							aria-live='assertive'
						>
							{STRINGS?.[error] || error}
						</Box>
					)}
					<Input
						value={context.challengeCode}
						placeholder={STRINGS.enterCodeProvided}
						onChange={onHandleContextChange('challengeCode')}
						id='mfa-challenge-code'
						type='text'
						color={inputColor}
						stretched
						aria-required='true'
						autoFocus
					/>
					<Button
						id='next-button'
						size='lg'
						disabled={!isValid}
						aria-disabled={!isValid}
						onClick={submitChallengeCode}
					>
						{STRINGS.continue}
					</Button>
					{(!isChange || showTryAnotherMethod) && (
						<Typography
							variant='link'
							component='a'
							href='#'
							align='center'
							onClick={handleChangeMethod}
						>
							{STRINGS.tryAnotherMethod}
						</Typography>
					)}
				</Box>
			</Box>
		</Slide>
	);
};

TOTPEnrollment.propTypes = {
	isLoaded: PropTypes.bool.isRequired,
	context: PropTypes.object.isRequired,
	setContext: PropTypes.func.isRequired,
	error: PropTypes.string,
	isSliding: PropTypes.bool.isRequired,
	setIsSliding: PropTypes.func.isRequired,
	handleChangeMethod: PropTypes.func.isRequired,
	handleErrorResponse: PropTypes.func.isRequired,
	handleError: PropTypes.func.isRequired,
	currentPage: PropTypes.string.isRequired,
	setCurrentPage: PropTypes.func.isRequired,
	isChange: PropTypes.bool,
};

TOTPEnrollment.defaultProps = {	error: '', isChange: false };

export default TOTPEnrollment;
